/**
 * The audio state machine, shared verbatim by web and iOS.
 *
 * Pure by contract: no DOM, no React, no next, no server-only. iOS will reach
 * this file as `@shared-lib/voice/audioContract`.
 *
 * HOW MUCH OF THAT IS ENFORCED: `src/ios/scripts/check-lib-purity.mjs` lists
 * this file in SHARED_CONTRACT_ROOTS, so `guard:purity` walks it in CI even
 * though no native file imports it yet - that guard is a reachability walk, and
 * without the entry an unreachable contract is simply never looked at. What it
 * rejects is a SERVER-reachable import: next, prisma, server-only, and anything
 * transitively behind them.
 *
 * The rest of the promise is not machine-checked. `react` is a legitimate
 * import inside src/ios, so the guard has no reason to reject it here, and DOM
 * usage is not an import at all. No DOM and no React in this file is a rule
 * review keeps; iOS `tsc` only backs it up once a native file imports this one.
 *
 * Exclusivity lives HERE rather than in the adapters: a `requested` event from
 * any state moves to `loading` with the new item, so there is no representable
 * state in which two items sound at once.
 */

export type AudioIntent = "tutor-speech" | "lesson" | "activity" | "preview";

export type AudioErrorReason = "timeout" | "load" | "blocked";

export interface AudioItem {
  /** Stable per request; the adapter uses it to drop events from a superseded clip. */
  id: string;
  intent: AudioIntent;
  /** What the tutor surface names as the thing currently sounding. */
  label: string;
  replayable: boolean;
  rateLocked: boolean;
}

export type AudioState =
  | { status: "idle" }
  | { status: "loading"; item: AudioItem }
  | { status: "playing"; item: AudioItem; rate: number }
  | { status: "paused"; item: AudioItem; rate: number }
  | { status: "error"; item: AudioItem; reason: AudioErrorReason };

export type AudioEvent =
  | { type: "requested"; item: AudioItem; rate: number }
  | { type: "started" }
  | { type: "paused" }
  | { type: "resumed" }
  | { type: "rate-changed"; rate: number }
  | { type: "ended" }
  | { type: "stopped" }
  | { type: "failed"; reason: AudioErrorReason };

/**
 * Why a clip left the bus, told to the caller that was awaiting it.
 *
 * `playAudio` settles when the clip leaves the bus, and four different things cause
 * that. The caller's next step differs for every one of them: chaining a second
 * clip, revealing comprehension questions, opening a record gate and starting an
 * answer clock are all correct after "ended" and wrong after the other three.
 *
 * Before this existed, callers re-read `getAudioState()` after the await. That read
 * can only ask "did it fail?", and it answers even that badly: a natural end and a
 * learner's Stop both reduce to `idle` and are indistinguishable, while a clip that
 * was superseded has already had its state replaced by the clip that replaced it, so
 * the read describes somebody else's playback.
 */
export type AudioOutcome = "ended" | "stopped" | "superseded" | "failed";

/**
 * The outcome an awaiting caller learns from the event that took its clip off the
 * bus; null for an event that leaves the clip on the bus.
 *
 * Derived from the EVENT, never from the state the event produces. That is the whole
 * point: `ended` and `stopped` reduce to the same `idle` state, so no amount of
 * reading state afterwards can separate them, and `requested` reduces to a `loading`
 * that describes the NEW clip rather than the superseded one.
 *
 * `requested` is an outcome for the PREVIOUS caller only. The bus settles the
 * outstanding promise before it installs the new one, so the mapping is never
 * ambiguous about whose outcome it is.
 */
export function audioOutcomeOf(event: AudioEvent): AudioOutcome | null {
  switch (event.type) {
    case "requested":
      return "superseded";
    case "ended":
      return "ended";
    case "stopped":
      return "stopped";
    case "failed":
      return "failed";
    // The clip is still on the bus after these, so nobody settles.
    case "started":
    case "paused":
    case "resumed":
    case "rate-changed":
      return null;
  }
}

export const IDLE_AUDIO_STATE: AudioState = { status: "idle" };

/** The rate the machine carries while nothing is loaded yet. */
const DEFAULT_RATE = 1;

export function audioReducer(state: AudioState, event: AudioEvent): AudioState {
  switch (event.type) {
    // Interruption is the normal case, not an error: requesting always wins.
    case "requested":
      return { status: "loading", item: event.item };

    case "started":
      return state.status === "loading"
        ? { status: "playing", item: state.item, rate: DEFAULT_RATE }
        : state;

    case "paused":
      return state.status === "playing"
        ? { status: "paused", item: state.item, rate: state.rate }
        : state;

    case "resumed":
      return state.status === "paused"
        ? { status: "playing", item: state.item, rate: state.rate }
        : state;

    case "rate-changed":
      return state.status === "playing" || state.status === "paused"
        ? { ...state, rate: event.rate }
        : state;

    case "ended":
    case "stopped":
      return IDLE_AUDIO_STATE;

    case "failed":
      return state.status === "idle"
        ? state
        : { status: "error", item: itemOf(state), reason: event.reason };
  }
}

function itemOf(state: Exclude<AudioState, { status: "idle" }>): AudioItem {
  return state.item;
}
